import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useApi } from "@/core/api";
import { useApiFileHref } from "@/core/api/api-file-href";
import { invoiceDocumentQueryOptions } from "./query";

/**
 * Fetches an AP invoice's document and exposes a same-origin `blob:` object URL
 * for embedding in an <iframe>/<img>.
 *
 * Why fetch the bytes instead of pointing the embed at `file_url` directly?
 * `file_url` is a root-relative ActiveStorage path (e.g. `/rails/active_storage/...`).
 * In local dev the Vite `/rails` proxy makes it same-origin, but in deployed
 * environments the SPA is served from a different origin than Rails (e.g.
 * app-staging / *.pages.dev vs staging), so:
 *   - a relative path resolves against the SPA origin and 404s (the CDN's SPA
 *     fallback returns index.html, which the iframe renders blank), and
 *   - an absolute cross-origin URL is blocked from framing by X-Frame-Options.
 * Fetching the bytes and framing a `blob:` URL sidesteps both — a blob URL is
 * same-origin to the page in every environment. Mirrors the download pattern
 * used by payroll/QR-code exports.
 */
export const useApInvoiceDocument = (fileUrl?: string | null) => {
  const api = useApi();

  const documentSrc = useApiFileHref(fileUrl);

  const {
    data: blob,
    isLoading,
    isError,
  } = useQuery(invoiceDocumentQueryOptions(api, documentSrc));

  // Object URLs must be revoked to avoid leaking the underlying blob; tie their
  // lifetime to the fetched blob.
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!blob) {
      setObjectUrl(null);
      return;
    }
    const url = URL.createObjectURL(blob);
    setObjectUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [blob]);

  return { objectUrl, isLoading, isError };
};
